import { Component, effect, inject } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { UserStore } from '../../../stores/user/user.state';
import {
  ToolAccess,
  ToolAccessKey,
  User,
  userLevelOptions,
  UserWorkspace,
  ViewAccess,
  ViewAccessKey,
} from '../../../models/user';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { NgForOf, NgIf } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';
import { SharedModule } from 'primeng/api';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { InputSwitchModule } from 'primeng/inputswitch';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { WorkspaceStore } from '../../../stores/workspace/workspace.state';
import { RoleStore } from '../../../stores/role/role.state';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { ActivatedRoute, Router } from '@angular/router';
import { AppUtilityService } from '../../../utility/app.utility';
import { RoutingService } from '../../../services/routing.service';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { AlertComponent } from '../../atoms/alert/alert.component';
import {
  asWorkspaceTypeKey,
  workspaceTypeIconMap,
} from '../../../models/workspace';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { getState } from '@ngrx/signals';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { DropdownOptionComponent } from '../../atoms/dropdown-option/dropdown-option.component';
import { HeaderComponent } from '../../atoms/header/header.component';

@Component({
  selector: 'app-user-form',
  standalone: true,
  imports: [
    DropdownModule,
    InputTextModule,
    NgIf,
    PaginatorModule,
    ReactiveFormsModule,
    SharedModule,
    FormFieldWrapperComponent,
    InputSwitchModule,
    DividerModule,
    ButtonModule,
    NgForOf,
    MultiSelectModule,
    CheckboxModule,
    AlertComponent,
    FormFooterComponent,
    DropdownOptionComponent,
    HeaderComponent,
  ],
  templateUrl: './user-form.component.html',
  styleUrl: './user-form.component.scss',
})
export class UserFormComponent extends BaseFormComponent<User> {
  readonly store = inject(UserStore);
  readonly activeUserStore = inject(ActiveUserStore);
  readonly workspaceStore = inject(WorkspaceStore);
  readonly roleStore = inject(RoleStore);
  override form: FormGroup = new FormGroup({});
  override itemToEdit?: User;
  initialized = false;
  communeFromRoute: string | null = null;

  toolAccess: ToolAccessKey[] = [
    { icon: 'fas fa-plus', name: 'Neu anlegen', key: 'entityAdder' },
    {
      icon: 'fas fa-users',
      name: 'Nutzer:innen verwalten',
      key: 'userManagement',
    },
    { icon: 'fas fa-gears', name: 'Konfiguration', key: 'config' },
    {
      icon: 'fas fa-folder-tree',
      name: 'Indikatorenverzeichnis',
      key: 'indicators',
    },
    { icon: 'fas fa-pen-ruler', name: 'Indikatorenvorlagen', key: 'pool' },
    { icon: 'fas fa-book-open', name: 'Themen', key: 'themes' },
    { icon: 'fas fa-note-sticky', name: 'Ansichten', key: 'bookmarks' },
    { icon: 'fas fa-file-lines', name: 'Berichte', key: 'stories' },
    { icon: 'fas fa-palette', name: 'Farbsets', key: 'colorsets' },
    {
      icon: 'fas fa-cloud-upload',
      name: 'Import/ Export',
      key: 'importExport',
    },
    { icon: 'fas fa-list-check', name: 'Sammelaktionen', key: 'massActions' },
    { icon: 'fas fa-clock-rotate-left', name: 'Historie', key: 'history' },
  ];

  viewAccess: ViewAccessKey[] = [
    { icon: 'fas fa-globe', name: 'Atlas', key: 'atlas' },
    { icon: 'fas fa-database', name: 'Datenpflege', key: 'dataBackend' },
    { icon: 'fas fa-inbox', name: 'Nachrichten', key: 'messages' },
  ];

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );
    this.roleStore.loadAllItemsByPromise();
    this.initialized = false;

    effect(() => {
      const state = getState(this.store);
      this.communeFromRoute =
        this.routingService.getParameterFromRoute('commune');
      if (this.communeFromRoute) {
        const userId = this.routingService.getIdFromRoute();
        if (!userId || userId === 'neu') {
          this.initializeForm();
        } else if (userId) {
          this.fetchUser(userId || 'neu');
        }
      } else if (state.selectedId && !this.initialized) {
        this.initializeForm();
      }
    });
  }

  /**
   * Initializes and configures a reactive form group for managing user data.
   * The method fetches user data when a specified condition is met, processes access permissions for tools
   * and views, and populates the form group with the appropriate default values.
   *
   * @return {void} This method does not return a value but sets up and populates the form, setting the `initialized` flag to true.
   */
  initializeForm(): void {
    if (!this.communeFromRoute) {
      this.itemToEdit = this.store.selectedItem();
    }

    const toolAccessControls = this.toolAccess.reduce(
      (acc, curr) => {
        const key = curr.key as keyof ToolAccess; // Explicitly cast to keyof ToolAccess
        acc[key] =
          this.itemToEdit && this.itemToEdit.toolAccess
            ? this.itemToEdit.toolAccess[key] // Now 'key' is narrowed down properly
            : false;
        return acc;
      },
      {} as Record<keyof ToolAccess, boolean>
    );

    const viewAccessControls = this.viewAccess.reduce(
      (acc, curr) => {
        const key = curr.key as keyof ViewAccess; // Explicitly cast to keyof ToolAccess
        acc[key] =
          this.itemToEdit && this.itemToEdit.viewAccess
            ? this.itemToEdit.viewAccess[key] // Now 'key' is narrowed down properly
            : false;
        return acc;
      },
      {} as Record<keyof ViewAccess, boolean>
    );

    this.form = this.fb.group({
      firstName: [this.itemToEdit?.firstName || '', Validators.required],
      lastName: [this.itemToEdit?.lastName || '', Validators.required],
      email: [this.itemToEdit?.email || '', Validators.required],
      username: [this.itemToEdit?.username || ''],
      phone: [this.itemToEdit?.phone || '', Validators.required],
      locked: [!(this.itemToEdit && !this.itemToEdit.locked)],
      isAdmin: [(this.itemToEdit && this.itemToEdit.isAdmin) || false],
      toolAccess: this.fb.group(toolAccessControls),
      viewAccess: this.fb.group(viewAccessControls),
      workspaces: this.fb.array([]),
      level: [this.itemToEdit?.level || 10],
    });

    this.itemToEdit?.workspaces.forEach(workspace =>
      this.addWorkspace(workspace)
    );
    this.initialized = true;
  }

  /**
   * Retrieves the 'workspaces' form array from the form group.
   *
   * @return {FormArray} The FormArray associated with the 'workspaces' control in the form group.
   */
  get workspacesFormArray(): FormArray {
    return this.form.get('workspaces') as FormArray;
  }

  /**
   * Adds a new workspace to the workspaces form array. If a workspace is provided,
   * its details will be pre-filled; otherwise, a blank workspace will be added.
   *
   * @param {UserWorkspace} [workspace] - The workspace object containing initial values for the form.
   *                                       Optional parameter. If not provided, defaults to a blank workspace.
   * @return {void} - This method does not return a value.
   */
  addWorkspace(workspace?: UserWorkspace): void {
    const workspaceGroup = this.fb.group({
      workspace: [workspace ? workspace.workspace : '', Validators.required],
      roles: [workspace ? workspace.roles : [], Validators.required],
    });
    this.workspacesFormArray.push(workspaceGroup);
  }

  /**
   * Removes a workspace from the workspaces form array at the specified index.
   *
   * @param {number} index - The zero-based index of the workspace to be removed from the form array.
   * @return {void} Does not return a value, modifies the workspaces form array directly.
   */
  removeWorkspace(index: number): void {
    this.workspacesFormArray.removeAt(index);
  }

  /**
   * Handles the form submission event by determining if a commune value from the route
   * should overwrite the form value before submitting. If the form is related to a new user,
   * a workaround to set the initial password is logged after a delay.
   *
   * @return {void} No return value.
   */
  onSubmit(): void {
    if (this.communeFromRoute) {
      const formOverwriteValue = this.form.value;
      formOverwriteValue.commune = this.communeFromRoute;
      this.submit(formOverwriteValue);
    } else {
      this.submit();
    }

    // todo: local workaround new user initial password
    setTimeout(() => {
      if (!this.itemToEdit) {
        console.log('set initial password here:');
        console.log(
          `http://localhost:4200/initiales-passwort/${this.store.lastPersistedId()}`
        );
      }
    }, 1000);
  }

  /**
   * Fetches a user's data by their ID and initializes the form with the user's information if found.
   *
   * @param {string} id - The unique identifier of the user to fetch.
   * @return {void} This method does not return any value.
   */
  fetchUser(id: string): void {
    if (this.activeUserStore.isSuperAdmin() && id !== 'neu') {
      this.store.fetchById(id).then(user => {
        if (user) {
          this.itemToEdit = user;
          this.initializeForm();
        } else {
          this.itemToEdit = undefined;
          this.initializeForm();
        }
      });
    }
  }

  protected readonly workspaceTypeIconMap = workspaceTypeIconMap;
  protected readonly asWorkspaceTypeKey = asWorkspaceTypeKey;
  protected readonly userLevelOptions = userLevelOptions;
}
